/* eslint-disable react-hooks/rules-of-hooks */
import React, { useMemo } from 'react';
import {
  AccountStatus,
  BoxExtendedProps,
  CardTypesEnum,
  costFormater,
  Enrollment,
  EnrollmentCard,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import colors from '@/styles/colors';

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

const LABELS = {
  election: 'Election',
  availableCashBalance: 'Available cash balance',
  availableBalance: 'Available balance',
};

interface Props extends BoxExtendedProps {
  enrollment: Enrollment;
  pendingTradesAmount?: number;
  investorEnrollmentId?: string;
}
const EnrollmentCardBlock: React.FC<Props> = ({
  enrollment,
  pendingTradesAmount = 0,
  investorEnrollmentId,
  ...rest
}) => {
  if (!enrollment) {
    return null;
  }

  const currentType = useMemo(() => {
    return enrollment?.accountType;
  }, [enrollment]);

  const remainingRolloverDate = useMemo(() => {
    return enrollment?.remainingRolloverDate
      ? dayjs(enrollment.remainingRolloverDate).format(DEFAULT_DATE_FORMAT)
      : undefined;
  }, [enrollment?.remainingRolloverDate]);

  const isDisabled = useMemo(() => {
    return enrollment.accountStatus === AccountStatus.CLOSED;
  }, [enrollment.accountStatus]);

  const subTitleColor = useMemo(() => colors.cardTypes[currentType], [currentType]);
  const showPendingTrades = useMemo(
    () => pendingTradesAmount > 0 && investorEnrollmentId === enrollment?.id,
    [pendingTradesAmount, investorEnrollmentId, enrollment]
  );
  return (
    <EnrollmentCard type={currentType} isDisabled={isDisabled} {...rest}>
      <EnrollmentCard.Icon type={currentType} />
      <EnrollmentCard.TopbarInfo>
        <EnrollmentCard.TopbarInfo.Status
          isCIPRequired={enrollment.isCIPRequired}
          policyStatus={enrollment.enrollmentStatus}
          accountStatus={enrollment.accountStatus}
          currentType={enrollment.accountType}
          CIPStatus={enrollment.CIPStatus}
        />
      </EnrollmentCard.TopbarInfo>
      <EnrollmentCard.Title>{enrollment?.name || ''}</EnrollmentCard.Title>
      <EnrollmentCard.SubTitle textColor={subTitleColor}>
        {enrollment?.displayName || ''}
      </EnrollmentCard.SubTitle>
      <EnrollmentCard.Item margin={{ top: 'spacing16', bottom: 'spacing8' }}>
        <EnrollmentCard.Item.Text testId="election">{LABELS.election}</EnrollmentCard.Item.Text>
        <EnrollmentCard.Item.Amount testId="election">
          {costFormater(enrollment?.election, true)}
        </EnrollmentCard.Item.Amount>
      </EnrollmentCard.Item>
      {showPendingTrades && (
        <EnrollmentCard.Item>
          <EnrollmentCard.Item.Text testId="pending_trade">Pending trade</EnrollmentCard.Item.Text>
          <EnrollmentCard.Item.Amount testId="pending_trade" showPendingTrades={showPendingTrades}>
            {costFormater(pendingTradesAmount, true)}
          </EnrollmentCard.Item.Amount>
        </EnrollmentCard.Item>
      )}
      <EnrollmentCard.Item bigSize margin={{ bottom: 'none' }}>
        <EnrollmentCard.Item.Text testId="available_balance">
          {currentType === CardTypesEnum.HSA ? LABELS.availableCashBalance : LABELS.availableBalance}
        </EnrollmentCard.Item.Text>
        <EnrollmentCard.Item.Amount
          testId="available_balance"
          remainingRollover={enrollment?.remainingRollover}
          remainingRolloverDate={remainingRolloverDate}
          size="2xl"
        >
          {costFormater(enrollment?.available, true)}
        </EnrollmentCard.Item.Amount>
      </EnrollmentCard.Item>
    </EnrollmentCard>
  );
};

export default EnrollmentCardBlock;
